import { Auction } from '@/models/registry';
import type { Auction as AuctionType } from '@/models/types';
import { makeAutoObservable } from 'mobx';
import { db } from '../db';
import type { DeltaPacketRecord } from '../hydrate';
import type { BaseStore } from './base';

export class AuctionStore implements BaseStore<Auction, AuctionType> {
  store: Map<string, Auction> = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  private setAuctionField = <K extends keyof AuctionType>(
    auction: Auction,
    key: K,
    value: AuctionType[K],
  ) => {
    (auction as Pick<Auction, K>)[key] = value as Auction[K];
  };

  applyDeltaV2({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<AuctionType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'delete' || delta.action === 'archive') {
      if (hydrateIndexedDB) {
        db.auctions.delete(delta.record_id);
      }
      this.remove(delta.record_id);
      return;
    }

    if (delta.action === 'create') {
      if (hydrateIndexedDB) {
        db.auctions.put(delta.data, delta.record_id);
      }
      this.upsert(delta.data);
      return;
    }

    if (delta.action === 'update') {
      if (hydrateIndexedDB) {
        db.auctions.update(delta.record_id, delta.data);
      }
      this.replaceOnlyUpdatedFields({ delta });
      return;
    }

    if (hydrateIndexedDB) {
      db.auctions.put(delta.data, delta.record_id);
    }
    this.upsert(delta.data);
  }

  replaceOnlyUpdatedFields({
    delta,
  }: {
    delta: DeltaPacketRecord<AuctionType>;
  }) {
    const existingAuction = this.getById(delta.record_id);
    if (!existingAuction) return;

    let changed = false;

    const entries = Object.entries(delta.data) as [
      keyof AuctionType,
      AuctionType[keyof AuctionType],
    ][];

    for (const [key, newValue] of entries) {
      if (newValue === undefined || existingAuction[key] === newValue) {
        continue;
      }

      this.setAuctionField(existingAuction, key, newValue);
      changed = true;
    }

    if (!changed) return;
    this.save(existingAuction);
  }

  getById(id: string): Auction | undefined {
    return this.store.get(id);
  }

  add(input: AuctionType) {
    const auction = new Auction(input);
    this.store.set(auction.id, auction);
  }

  // Computed getters - automatically update when store changes
  get all() {
    return Array.from(this.store.values());
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<AuctionType>[];
    hydrateIndexedDB?: boolean;
  }) {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<AuctionType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (hydrateIndexedDB) {
      db.auctions.put(delta.data, delta.record_id);
    }

    if (delta.action === 'delete') {
      this.remove(delta.record_id);
    }

    this.upsert(delta.data);
  }

  upsert(auction: AuctionType) {
    const existingAuction = this.getById(auction.id);
    if (existingAuction) {
      this.replace({ id: auction.id, auction });
    } else {
      this.add(auction);
    }
  }

  replace({ id, auction }: { id: string; auction: AuctionType }) {
    const existingAuction = this.getById(id);
    if (!existingAuction) return;

    existingAuction.name = auction.name;
    existingAuction.created_at = auction.created_at;
    existingAuction.updated_at = auction.updated_at;

    this.save(existingAuction);
  }

  save(auction: Auction) {
    this.store.set(auction.id, auction);
  }

  remove(id: string) {
    this.store.delete(id);

    // remove from dexie
    db.auctions.delete(id);
  }
}

export const auctionStore = new AuctionStore();
